/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import {
  Backdrop,
  CircularProgress,
  Button,
  Typography,
  Chip,
} from "@mui/material";
import Checklist from "../../components/Checklist";
import UploadZone from "../../components/UploadZone";
import BuilderSplitWidget from "../../components/BuilderSplitWidget";
import Comments from "../../modals/Comments";
import axiosInstance from "../../utils/axiosInstance";
import { useData } from "../../providers/DataProvider";
import {
  BuilderInfo,
  fileAttachmentsByTypesProps,
  JobDetailsProps,
} from "../../providers/types";
import { useMsal } from "@azure/msal-react";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { UIContext } from "../../providers/UIProvider";

export default function FileUploadList() {
  const [state, dispatch] = React.useContext<any>(UIContext);
  const { role, bonusType } = useData();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [job, setJob] = React.useState<JobDetailsProps | undefined>(undefined);
  const [jobBuilders, setJobBuilders] = React.useState<BuilderInfo[]>([]);
  const [ableToEdit, setAbletoEdit] = React.useState<boolean>(false);
  const [ableToEditBuilder, setAbletoEditBuilder] =
    React.useState<boolean>(false);
  const [allBuilders, setAllBuilders] = React.useState<
    {
      builderId: number;
      builderEmail: string;
      builderName: string;
      builderNumber: number;
      employeeNumber: number;
    }[]
  >([]);
  const [selectedFile, setSelectedFile] =
    React.useState<fileAttachmentsByTypesProps | null>(null);
  const [readyToSubmit, setReadyToSubmit] = React.useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(320); // Default width

  const { id } = useParams();

  const selectChecklistItem = (file: fileAttachmentsByTypesProps) => {
    setSelectedFile(file);
  };

  const submitJob = () => {
    if (job) {
      if (
        (bonusType === "Stage5Bonus" && jobBuilders.length > 0) ||
        (selectedFile && checkRequiredAttachments())
      ) {
        if (!jobBuilders.length) {
          alert("Please assign a builder");
          return;
        }
        if (window.confirm("Are you sure you want to submit for review?")) {
          axiosInstance
            .put(`/api/cba/bonuses/${job.jobId}`, {
              JobId: job.jobId,
              BonusStatus: "Review",
              UpdatedBy: accounts[0].username,
            })
            .then(() => {
              dispatch({
                type: "Snackbar",
                payload: {
                  show: true,
                  message: "Successfully submitted for review",
                  severity: "success",
                },
              });
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            });
        }
      } else {
        alert("Please add all required attachments before submitting");
      }
    }
  };

  const checkRequiredAttachments = () => {
    if (!job) return false;
    const requiredAttachments = job.fileAttachmentsByTypes.filter(
      (item) => item.isRequired
    );
    return requiredAttachments.every((item) => item.fileAttachments.length > 0);
  };

  const handleNext = () => {
    if (selectedFile && job) {
      const requiredFiles = job.fileAttachmentsByTypes.filter(
        (file) => file.isRequired
      );
      const optionalFiles = job.fileAttachmentsByTypes.filter(
        (file) => !file.isRequired
      );
      const allFiles = [...requiredFiles, ...optionalFiles];

      const currentIndex = allFiles.findIndex(
        (item) =>
          item.fileAttachmentTypeId === selectedFile.fileAttachmentTypeId
      );

      const nextIndex = (currentIndex + 1) % allFiles.length;
      setSelectedFile(allFiles[nextIndex]);
    }
  };

  const reloadBuilders = () => {
    axiosInstance
      .get(`/api/cba/job-details/${id}`, {
        headers: {
          roleType: role,
        },
      })
      .then((response) => {
        setJob(response.data);
        setJobBuilders(response.data.builders);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching job details:", error);
        setLoading(false);
      });
  };

  const loadJobDetails = () => {
    axiosInstance
      .get(`/api/cba/job-details/${id}`, {
        headers: {
          roleType: role,
        },
      })
      .then((response) => {
        setJob(response.data);
        setJobBuilders(response.data.builders);
        setSelectedFile(response.data.fileAttachmentsByTypes[0]);

        const requiredIds = new Set(
          Array.from({ length: 11 }, (_, i) => i + 1)
        );
        const presentIds = new Set(
          response.data.fileAttachmentsByTypes.map(
            (item: { fileAttachmentTypeId: any }) => item.fileAttachmentTypeId
          )
        );
        setReadyToSubmit([...requiredIds].every((id) => presentIds.has(id)));

        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching job details:", error);
        setLoading(false);
      });
  };

  const reloadJob = () => {
    axiosInstance
      .get(`/api/cba/job-details/${id}`, {
        headers: {
          roleType: role,
        },
      })
      .then((response) => {
        setJob(response.data);
        setJobBuilders(response.data.builders);
        const requiredIds = new Set(
          Array.from({ length: 11 }, (_, i) => i + 1)
        );
        const presentIds = new Set(
          response.data.fileAttachmentsByTypes.map(
            (item: { fileAttachmentTypeId: any }) => item.fileAttachmentTypeId
          )
        );
        setReadyToSubmit([...requiredIds].every((id) => presentIds.has(id)));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching job details:", error);
        setLoading(false);
      });
  };

  const EX_EMPLOYEE_BUILDER = {
    builderId: 999999, // Add this property if needed
    builderName: "Ex Employee",
    builderNumber: 999999,
    employeeNumber: 999999,
    builderEmail: "noreply@highlandhomes.com",
  };

  const loadAllBuilders = () => {
    axiosInstance
      .post(`/api/cba/builders/search`)
      .then((response) => {
        setAllBuilders([EX_EMPLOYEE_BUILDER, ...response.data]);
      })
      .catch((error) => {
        console.error("Error fetching job builder details:", error);
      });
  };

  const formatStatus = (status: string) => {
    switch (status.trim()) {
      case "NotStarted":
        return "Not Started";
      case "InUploadProcess":
        return "Ready to Start";
      case "InPMReview":
        return "Awaiting PM Review";
      case "InSPMReview":
        return "Awaiting SPM Review";
      case "InDMReview":
        return "Awaiting DM Review";
      case "InOpsReview":
        return "Awaiting Ops Review";
      case "OpsApproved":
        return "Approved";
      case "Rejected":
        return "Rejected";
      case "Busted":
        return "Busted";
      case "InPayrollProcess":
        return "In Payroll Process";
      case "Paid":
        return "Paid";
      default:
        return status.length ? status : "Not Started";
    }
  };

  const getStatusColor = (status: string): "default" | "success" | "error" | "warning" | "info" => {
    const statusTrimmed = status.trim();
    if (["Rejected", "Busted"].includes(statusTrimmed)) {
      return "error";
    } else if (["OpsApproved", "Paid"].includes(statusTrimmed)) {
      return "success";
    } else if (["InPayrollProcess"].includes(statusTrimmed)) {
      return "info";
    } else if (["NotStarted", "InUploadProcess"].includes(statusTrimmed)) {
      return "warning";
    } else if (["InPMReview", "InSPMReview", "InDMReview", "InOpsReview"].includes(statusTrimmed)) {
      return "info";
    }
    return "default";
  };

  React.useEffect(() => {
    if (id) {
      loadJobDetails();
      loadAllBuilders();
    }
  }, [id]);

  useEffect(() => {
    if (job) {
      setAbletoEdit(
        bonusType === "Stage5Bonus" ||
          !job.reviewStatus ||
          job.reviewStatus.trim() === "Rejected" ||
          job.reviewStatus.trim() === "New" ||
          job.reviewStatus.trim() === "InUploadProcess" ||
          job.reviewStatus.trim() === "NotStarted"
      );
    }
  }, [job, bonusType]);

  useEffect(() => {
    if (job) {
      setAbletoEditBuilder(
        job.reviewStatus.trim() === "Rejected" ||
          job.reviewStatus.trim() === "New" ||
          job.reviewStatus === "" ||
          job.reviewStatus.trim() === "InUploadProcess" ||
          job.reviewStatus.trim() === "NotStarted"
      );
    }
  }, [job]);

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar - Only show for ClosingBonus */}
      {bonusType === "ClosingBonus" && (
        <div
          className="flex-shrink-0 border-r border-gray-200 overflow-hidden flex flex-col"
          style={{ width: `${sidebarWidth}px` }}
        >
          <Checklist
            job={job}
            selectChecklistItem={selectChecklistItem}
            selectedFile={selectedFile}
            drawerWidth={sidebarWidth}
            onWidthChange={setSidebarWidth}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-grow overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h6">{`${job?.jobAddress} - ${job?.projectNumber}`}</Typography>
            <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <strong>Status:</strong>
              <Chip
                label={formatStatus(job?.reviewStatus ?? "")}
                color={getStatusColor(job?.reviewStatus ?? "")}
                size="small"
                sx={{
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              />
            </Typography>
          </div>
          {job && (
            <BuilderSplitWidget
              job={job}
              jobBuilders={jobBuilders}
              allBuilders={allBuilders}
              setJobBuilders={setJobBuilders}
              reloadBuilders={reloadBuilders}
              active={ableToEditBuilder}
            />
          )}
        </div>

        {/* Main Content Area - Only show UploadZone for ClosingBonus */}
        <div className="flex-grow overflow-auto">
          {bonusType === "ClosingBonus" && job && selectedFile && (
            <UploadZone
              job={job}
              reloadJob={reloadJob}
              selectChecklistItem={selectChecklistItem}
              selectedFile={selectedFile}
            />
          )}
          {bonusType === "Stage5Bonus" && (
            <div className="flex items-center justify-center h-full">
              <Typography variant="h6" color="textSecondary">
                File uploads are not required for Stage 5 Bonus
              </Typography>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white">
          <div className="flex justify-between items-center p-4">
            <div>{job && <Comments jobId={job.jobId} />}</div>
            <div>
              {bonusType === "ClosingBonus" && (
                <Button onClick={handleNext} sx={{ mr: 1 }}>
                  Next
                </Button>
              )}
              {bonusType === "ClosingBonus" && (
              <Button
                variant="contained"
                onClick={submitJob}
                disabled={
                    !ableToEdit
                }>
                Submit
              </Button>
               )}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </div>
  );
}
